import { useEffect, useMemo, useRef, useState } from "react";
import * as Sentry from "@sentry/react";
import Card from "../components/Card";
import Dropdown from "../components/Dropdown";
import DateRangePicker from "../components/DateRangePicker";
import api from "../api/client";
import { useAuth } from "../contexts/AuthContext";
import "leaflet/dist/leaflet.css";
import "leaflet.markercluster/dist/MarkerCluster.css";
import "leaflet.markercluster/dist/MarkerCluster.Default.css";
import "./styles.css";

const TEMPORADA_CACHE_KEY = "visor_temporada";

function getCurrentTemporada() {
    const now = new Date();
    const month = now.getMonth() + 1; // 1-12
    const year = now.getFullYear();
    return month >= 4 ? year : year - 1;
}

function getTemporadaOptions() {
    const current = getCurrentTemporada();
    // Mostrar la 1 temporada anterior + la actual + la siguiente
    const options = [];
    for (let y = current - 1; y <= current + 1; y++) {
        options.push(y);
    }
    return options;
}

function getInitialTemporada() {
    const cached = localStorage.getItem(TEMPORADA_CACHE_KEY);
    if (cached) {
        const parsed = parseInt(cached, 10);
        if (!isNaN(parsed)) return parsed;
    }
    return getCurrentTemporada();
}

function temporadaToRange(year) {
    return {
        startDate: `${year}-04-01`,
        endDate: `${year + 1}-03-31`,
    };
}

export default function Visor() {
    const mapRef = useRef(null);
    const mapInstanceRef = useRef(null);
    const clusterGroupRef = useRef(null);
    const { user, permissions } = useAuth();
    const [geometrias, setGeometrias] = useState(null);
    const [layers, setLayers] = useState([]);
    const [loadingGeo, setLoadingGeo] = useState(false);
    const [error, setError] = useState(null);
    const overlayLayersRef = useRef([]);
    const cotoHighlightRef = useRef(null);
    const [temporada, setTemporada] = useState(getInitialTemporada);
    const [cotos, setCotos] = useState([]);
    const [selectedCoto, setSelectedCoto] = useState(null);
    const [dateRange, setDateRange] = useState(() =>
        temporadaToRange(getInitialTemporada()),
    );
    const [cotoMsg, setCotoMsg] = useState(null);
    const [loadingCoto, setLoadingCoto] = useState(false);
    const [showHeatmap, setShowHeatmap] = useState(false);
    const [heatPoints, setHeatPoints] = useState(null);
    const heatLayerRef = useRef(null);
    const [especies, setEspecies] = useState([]);
    const [selectedEspecies, setSelectedEspecies] = useState([]);
    // Ids separados por comas (formato que espera el backend); undefined = todas.
    const especiesParam = selectedEspecies.length > 0 ? selectedEspecies.join(",") : undefined;

    const canSeeCapturas = permissions?.canViewCapturasPoints;
    const cotoOptions = useMemo(
        () => cotos.map((c) => ({ value: c.label, label: c.label })),
        [cotos],
    );

    // Cursor "ruleta" (progress) en toda la página mientras se carga geo o un coto.
    useEffect(() => {
        const busy = loadingGeo || loadingCoto;
        document.body.style.cursor = busy ? 'progress' : '';
        return () => {
            document.body.style.cursor = '';
        };
    }, [loadingGeo, loadingCoto]);

    // Sincronizar la temporada (usada en geo-data y feature-info) con el
    // dateRange. Cuando el usuario cambia las fechas, se determina el año
    // de la temporada en base al startDate (abril marca el inicio de la
    // siguiente temporada cinegética).
    useEffect(() => {
        if (!dateRange.startDate) return;
        const [y, m] = dateRange.startDate.split("-").map((v) => parseInt(v, 10));
        if (Number.isFinite(y) && Number.isFinite(m)) {
            const t = m >= 4 ? y : y - 1;
            if (t !== temporada) {
                setTemporada(t);
                localStorage.setItem(TEMPORADA_CACHE_KEY, String(t));
            }
        }
    }, [dateRange.startDate, temporada]);

    // Construye el HTML del popup con las capturas agrupadas por especie/sexo.
    const buildCotoPopupHtml = (matricula, capturas, currentTemporada) => {
        let html = `<div style="max-height:300px;overflow:auto;font-size:13px;min-width:220px">`;
        html += `<strong style="font-size:14px">Coto: ${matricula}</strong>`;
        html += `<div style="color:#888;font-size:11px;margin-bottom:4px">Temporada ${currentTemporada}/${currentTemporada + 1}</div>`;

        if (capturas && capturas.length > 0) {
            const porEspecie = {};
            capturas.forEach((c) => {
                if (!porEspecie[c.nombre_especie]) {
                    porEspecie[c.nombre_especie] = { machos: 0, hembras: 0, otros: 0, total: 0 };
                }
                const sexo = c.sexo?.toLowerCase();
                if (sexo === "m") porEspecie[c.nombre_especie].machos += c.total;
                else if (sexo === "f") porEspecie[c.nombre_especie].hembras += c.total;
                else porEspecie[c.nombre_especie].otros += c.total;
                porEspecie[c.nombre_especie].total += c.total;
            });

            html += `<table style="width:100%;border-collapse:collapse;margin-top:8px;font-size:12px">`;
            html += `<tr style="background:#f0f0f0"><th style="text-align:left;padding:4px">Especie</th><th style="padding:4px; text-align:center;">♂</th><th style="padding:4px; text-align:center;">♀</th><th style="padding:4px; text-align:center;">Total</th></tr>`;

            let grandTotal = 0;
            Object.entries(porEspecie).forEach(([especie, counts]) => {
                html += `<tr style="border-bottom:1px solid #eee">`;
                html += `<td style="padding:4px">${especie}</td>`;
                html += `<td style="text-align:center;padding:4px">${counts.machos || " "}</td>`;
                html += `<td style="text-align:center;padding:4px">${counts.hembras || " "}</td>`;
                html += `<td style="text-align:center;padding:4px;font-weight:bold">${counts.total}</td>`;
                html += `</tr>`;
                grandTotal += counts.total;
            });

            html += `<tr style="background:#e8f4f8;font-weight:bold"><td style="padding:4px">Total</td><td colspan="2"></td><td style="text-align:center;padding:4px">${grandTotal}</td></tr>`;
            html += `</table>`;
        } else {
            html += `<p style="color:#666;margin-top:8px">Sin capturas registradas</p>`;
        }

        html += `</div>`;
        return html;
    };

    // Selector de matrícula: hace zoom al coto y abre automáticamente el popup
    // con el resumen de capturas. Lanzado desde onChange para que también
    // funcione re-seleccionando el mismo coto.
    const handleCotoSelect = async (matricula) => {
        setSelectedCoto(matricula);
        setCotoMsg(null);
        if (!matricula) {
            if (cotoHighlightRef.current && mapInstanceRef.current) {
                mapInstanceRef.current.removeLayer(cotoHighlightRef.current);
                cotoHighlightRef.current = null;
            }
            return;
        }
        setLoadingCoto(true);
        try {
            const params = { matricula, especies: especiesParam };
            if (dateRange.startDate && dateRange.endDate) {
                params.start_date = dateRange.startDate;
                params.end_date = dateRange.endDate;
            } else {
                params.temporada = temporada;
            }

            const response = await api.get("/visor/coto-bounds", { params });
            const data = response.data || {};
            const b = data.bounds;
            const center = data.center;

            if (!mapInstanceRef.current) return;

            if (b) {
                mapInstanceRef.current.fitBounds(
                    [
                        [b.south, b.west],
                        [b.north, b.east],
                    ],
                    { padding: [40, 40], maxZoom: 15 },
                );
            } else {
                setCotoMsg("No se pudo determinar la ubicación del coto.");
                return;
            }

            const L = (await import("leaflet")).default;

            // Destacar el contorno del coto seleccionado
            if (cotoHighlightRef.current) {
                mapInstanceRef.current.removeLayer(cotoHighlightRef.current);
                cotoHighlightRef.current = null;
            }
            const polys = Array.isArray(data.polygons) ? data.polygons : [];
            if (polys.length > 0) {
                const latlngs = polys
                    .filter(ring => Array.isArray(ring) && ring.length >= 3)
                    .map(ring => ring.map(p => [parseFloat(p[1]), parseFloat(p[0])]));

                const group = L.featureGroup();
                // Halo blanco para destacar sobre cualquier fondo
                L.polygon(latlngs, {
                    color: '#FFFFFF',
                    weight: 9,
                    opacity: 0.9,
                    fill: false,
                    interactive: false,
                }).addTo(group);
                // Contorno principal
                L.polygon(latlngs, {
                    color: '#0B3B2E',
                    weight: 4,
                    opacity: 1,
                    fillColor: '#1C8153',
                    fillOpacity: 0.2,
                    interactive: false,
                }).addTo(group);
                group.addTo(mapInstanceRef.current);
                cotoHighlightRef.current = group;
            }

            // Abrir popup en el centro con el resumen de capturas
            if (center) {
                const html = buildCotoPopupHtml(matricula, data.capturas || [], temporada);
                L.popup({ maxWidth: 350 })
                    .setLatLng([center.lat, center.lng])
                    .setContent(html)
                    .openOn(mapInstanceRef.current);
            }
        } catch (err) {
            if (err.response?.status === 404) {
                setCotoMsg(`Sin geometría disponible para ${matricula}.`);
            } else if (err.response?.status !== 401) {
                Sentry.captureException(
                    new Error("Error obteniendo bounds de coto: " + err.message),
                );
                setCotoMsg("Error al consultar el coto.");
            }
        } finally {
            setLoadingCoto(false);
        }
    };

    // Inicialización: mapa y layers (UNA SOLA VEZ al montar)
    useEffect(() => {
        // Inicializar mapa
        if (typeof window !== "undefined" && mapRef.current && !mapInstanceRef.current) {
            import("leaflet").then((L) => {
                const map = L.default
                    .map(mapRef.current, {
                        preferCanvas: true,
                        renderer: L.default.canvas(),
                        minZoom: 6,
                        maxBounds: [
                            [35.5, -9.5],  // Suroeste de España
                            [44.0, 4.5]     // Noreste de España
                        ],
                        maxBoundsViscosity: 1.0
                    })
                    .setView([41.5, -4.0], 8);

                const ignLayer = L.default
                    .tileLayer.wms(
                        "https://www.ign.es/wms-inspire/mapa-raster",
                        {
                            layers: 'mtn_rasterizado',
                            format: 'image/png',
                            transparent: false,
                            attribution: '© <a href="https://www.ign.es">Instituto Geográfico Nacional de España</a>',
                            maxZoom: 19,
                        },
                    );

                const osmLayer = L.default
                    .tileLayer(
                        "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
                        {
                            attribution: '© <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
                            maxZoom: 19,
                        },
                    );

                const ortoLayer = L.default
                    .tileLayer(
                        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
                        {
                        attribution: '© Esri',
                        maxZoom: 19,
                        }
                    );

                ignLayer.addTo(map);

                const baseMaps = {
                    "IGN - Mapa Raster": ignLayer,
                    "OpenStreetMap": osmLayer,
                    "Ortofoto": ortoLayer
                };

                const layerControl = L.default.control.layers(baseMaps, {}).addTo(map);
                map._layerControl = layerControl;

                mapInstanceRef.current = map;
            });
        }

        // Cargar layers del backend
        const fetchLayers = async () => {
            try {
                const response = await api.get('/visor/layers');
                setLayers(response.data);
            } catch (err) {
                Sentry.captureException(new Error('Error al cargar layers: ' + err.message));
            }
        };

        fetchLayers();

        return () => {
            if (mapInstanceRef.current) {
                mapInstanceRef.current.remove();
                mapInstanceRef.current = null;
            }
        };
    }, []);

    // Recargar capturas geo cuando cambie la temporada (solo admin)
    useEffect(() => {
        const fetchGeometrias = async () => {
            if (!canSeeCapturas) return;
            try {
                setLoadingGeo(true);
                const response = await api.get("/visor/geo-data", {
                    params: { temporada, especies: especiesParam },
                });
                if (clusterGroupRef.current && mapInstanceRef.current) {
                    mapInstanceRef.current.removeLayer(clusterGroupRef.current);
                    clusterGroupRef.current = null;
                }
                setGeometrias(response.data);
                setError(null);
            } catch (err) {
                Sentry.captureException(new Error('Error al cargar geometrías: ' + err.message));
                setError('No se pudieron cargar las geometrías');
            } finally {
                setLoadingGeo(false);
            }
        };

        fetchGeometrias();
    }, [temporada, canSeeCapturas, especiesParam]);

    // Cargar puntos del mapa de calor cuando el toggle está activo (solo admin)
    useEffect(() => {
        if (!canSeeCapturas || !showHeatmap) return;

        const fetchHeatmap = async () => {
            try {
                const response = await api.get("/visor/heatmap-data", {
                    params: { temporada, especies: especiesParam },
                });
                setHeatPoints(response.data);
            } catch (err) {
                Sentry.captureException(new Error('Error al cargar mapa de calor: ' + err.message));
            }
        };

        fetchHeatmap();
    }, [temporada, canSeeCapturas, showHeatmap, especiesParam]);

    // Cargar listado de cotos para el selector de matrícula
    useEffect(() => {
        const fetchCotos = async () => {
            try {
                const response = await api.get("/cotos");
                setCotos(response.data || []);
            } catch (err) {
                Sentry.captureException(new Error("Error al cargar cotos: " + err.message));
            }
        };
        fetchCotos();
    }, []);

    // Cargar listado de especies para el filtro
    useEffect(() => {
        const fetchEspecies = async () => {
            try {
                const response = await api.get("/especies");
                setEspecies(response.data || []);
            } catch (err) {
                Sentry.captureException(new Error("Error al cargar especies: " + err.message));
            }
        };
        fetchEspecies();
    }, []);

    // Agregar overlay layers al mapa cuando estén disponibles
    useEffect(() => {
        if (!mapInstanceRef.current || layers.length === 0) return;

        import("leaflet").then((L) => {
            const map = mapInstanceRef.current;
            const layerControl = map._layerControl;

            // Limpiar overlays anteriores
            overlayLayersRef.current.forEach(ol => {
                map.removeLayer(ol.layer);
                layerControl.removeLayer(ol.layer);
            });
            overlayLayersRef.current = [];

            layers.forEach(layerConfig => {
                if (layerConfig.type === 'wms') {
                    const wmsLayer = L.default.tileLayer.wms(layerConfig.url, layerConfig.params);
                    wmsLayer.addTo(map);
                    if (layerConfig.overlay) {
                        layerControl.addOverlay(wmsLayer, layerConfig.name);
                    }
                    overlayLayersRef.current.push({ layer: wmsLayer, config: layerConfig });
                }
            });

            // Registrar click handler (se reinstala cuando cambia temporada, layers o fechas)
            const registerClickHandler = (currentTemporada, currentRange, currentEspecies) => {
                if (map._wmsClickHandler) {
                    map.off('click', map._wmsClickHandler);
                    delete map._wmsClickHandler;
                }

                const onMapClick = async (e) => {
                    const hasVisibleWms = overlayLayersRef.current.some(ol => map.hasLayer(ol.layer));
                    if (!hasVisibleWms) return;

                    const bounds = map.getBounds();
                    const size = map.getSize();
                    const bbox = `${bounds.getWest()},${bounds.getSouth()},${bounds.getEast()},${bounds.getNorth()}`;
                    const point = map.latLngToContainerPoint(e.latlng);

                    try {
                        const params = {
                            bbox,
                            x: Math.round(point.x),
                            y: Math.round(point.y),
                            width: size.x,
                            height: size.y,
                            temporada: currentTemporada,
                            especies: currentEspecies,
                        };
                        if (currentRange?.startDate && currentRange?.endDate) {
                            params.start_date = currentRange.startDate;
                            params.end_date = currentRange.endDate;
                        }
                        const response = await api.get('/visor/feature-info', { params });

                        const data = response.data;
                        if (data && data.matricula) {
                            const html = buildCotoPopupHtml(
                                data.matricula,
                                data.capturas || [],
                                currentTemporada,
                            );
                            L.default.popup({ maxWidth: 350 })
                                .setLatLng(e.latlng)
                                .setContent(html)
                                .openOn(map);
                        }
                    } catch (err) {
                        // Silenciar errores de feature info (puede ser click fuera de polígonos)
                    }
                };

                map.on('click', onMapClick);
                map._wmsClickHandler = onMapClick;
            };

            registerClickHandler(temporada, dateRange, especiesParam);
        });

        return () => {
            const map = mapInstanceRef.current;
            if (map && map._wmsClickHandler) {
                map.off('click', map._wmsClickHandler);
                delete map._wmsClickHandler;
            }
        };
    }, [layers, temporada, dateRange.startDate, dateRange.endDate, especiesParam]);

    // Agregar datos al mapa cuando lleguen
    useEffect(() => {
        if (!geometrias || !mapInstanceRef.current) return;

        Promise.all([
            import("leaflet"),
            import("leaflet.markercluster")
        ]).then(([L]) => {
            const map = mapInstanceRef.current;

            // Convertir a formato GeoJSON si es un array
            const geojsonData = geometrias.features ? geometrias : {
                type: "FeatureCollection",
                features: geometrias
            };

            if (!geojsonData.features || geojsonData.features.length === 0) return;

            // Crear UN SOLO cluster group (eficiente)
            const clusterGroup = L.default.markerClusterGroup({
                maxClusterRadius: 80,
                spiderfyOnMaxZoom: true,
                showCoverageOnHover: false,
                zoomToBoundsOnClick: true,
                disableClusteringAtZoom: 18,
                chunkedLoading: true,
                chunkInterval: 100,
                chunkDelay: 30,
                iconCreateFunction: function(cluster) {
                    const count = cluster.getChildCount();
                    let size = 'small';
                    if (count >= 100) size = 'large';
                    else if (count >= 10) size = 'medium';

                    const sizeMap = { small: 30, medium: 40, large: 50 };
                    const diameter = sizeMap[size];

                    return L.default.divIcon({
                        html: `<div style="background-color: #3388ff; color: white; border-radius: 50%; width: ${diameter}px; height: ${diameter}px; display: flex; align-items: center; justify-content: center; font-weight: bold; border: 3px solid white; box-shadow: 0 2px 5px rgba(0,0,0,0.3); font-size: ${size === 'large' ? '14px' : '12px'};">${count}</div>`,
                        className: 'custom-cluster-icon',
                        iconSize: L.default.point(diameter, diameter)
                    });
                }
            });

            // Procesar GeoJSON directamente
            L.default.geoJSON(geojsonData, {
                pointToLayer: (feature, latlng) => {
                    return L.default.circleMarker(latlng, {
                        color: '#3388ff',
                        fillColor: '#3388ff',
                        fillOpacity: 0.7,
                        weight: 1,
                        radius: 6
                    });
                },
                onEachFeature: (feature, layer) => {
                    const props = feature.properties;
                    // Popup lazy loading
                    layer.on('click', function() {
                        if (!layer.getPopup()) {
                            const popupContent = props.popup || `
                                <strong>Precinto:</strong> ${props.precinto || 'N/A'}<br>
                                <strong>Especie:</strong> ${props.nombre_especie || 'N/A'}<br>
                                <strong>Sexo:</strong> ${props.sexo == 'm' ? 'Macho' : props.sexo == 'Hembra' ? 'Femenino' : 'N/A'}<br>
                                <strong>Matrícula:</strong> ${props.matricula_coto || 'N/A'}
                            `;
                            layer.bindPopup(popupContent).openPopup();
                        }
                    });

                    clusterGroup.addLayer(layer);
                }
            });

            // Añadir al mapa
            clusterGroup.addTo(map);
            clusterGroupRef.current = clusterGroup;

            // Ajustar vista inmediatamente
            try {
                map.invalidateSize();
                if (clusterGroup.getBounds && clusterGroup.getBounds().isValid()) {
                    map.fitBounds(clusterGroup.getBounds(), {
                        padding: [50, 50],
                        maxZoom: 12
                    });
                }
            } catch (err) {
                Sentry.captureException(new Error('Error ajustando bounds: ' + err.message));
            }
        });
    }, [geometrias]);

    // Pintar/actualizar la capa de mapa de calor y ocultar el cluster de
    // marcadores mientras esté activa (para no saturar el mapa).
    useEffect(() => {
        const map = mapInstanceRef.current;
        if (!map) return;

        if (showHeatmap && heatPoints && heatPoints.length > 0) {
            Promise.all([import("leaflet"), import("leaflet.heat")]).then(([L]) => {
                if (heatLayerRef.current) {
                    map.removeLayer(heatLayerRef.current);
                    heatLayerRef.current = null;
                }
                const heat = L.default.heatLayer(heatPoints, {
                    radius: 25,
                    blur: 20,
                    maxZoom: 17,
                });
                heat.addTo(map);
                heatLayerRef.current = heat;
            });
        } else if (heatLayerRef.current) {
            map.removeLayer(heatLayerRef.current);
            heatLayerRef.current = null;
        }

        if (clusterGroupRef.current) {
            if (showHeatmap && map.hasLayer(clusterGroupRef.current)) {
                map.removeLayer(clusterGroupRef.current);
            } else if (!showHeatmap && !map.hasLayer(clusterGroupRef.current)) {
                map.addLayer(clusterGroupRef.current);
            }
        }
    }, [showHeatmap, heatPoints]);

    return (
        <div className="page-general">
            <h1 className="page-title">Visor de Mapa</h1>

            <div className="page-header-section">
                <div className="page-header-title">
                    <h2 className="page-section-title">
                        Visualización geográfica
                    </h2>
                    <p className="page-section-subtitle">
                        Explora la ubicación de cotos y zonas de caza
                        {canSeeCapturas && !loadingGeo && geometrias && (geometrias.features ? ` (${geometrias.features.length} capturas cargadas)` : '')}
                    </p>
                </div>
            </div>

            <div
                className="page-content"
                style={loadingGeo ? { cursor: 'progress' } : undefined}
            >
                <Card>
                    <div
                        className="card-content"
                        style={{
                            position: 'relative',
                            cursor: loadingGeo ? 'progress' : undefined,
                        }}
                    >
                        {/* Filtros centrados: matrícula + fechas */}
                        <div style={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            gap: '14px',
                            marginBottom: '14px',
                        }}>
                            <div
                                style={{
                                    display: 'flex',
                                    alignItems: 'flex-end',
                                    justifyContent: 'center',
                                    gap: '24px',
                                    flexWrap: 'wrap',
                                }}
                            >
                                <div className="filter-group" style={{ minWidth: 240 }}>
                                    <label className="filter-label">Ir a coto (matrícula)</label>
                                    <Dropdown
                                        options={cotoOptions}
                                        value={selectedCoto}
                                        onChange={handleCotoSelect}
                                        placeholder="Selecciona un coto"
                                        searchable={true}
                                    />
                                </div>
                                <div className="filter-group" style={{ minWidth: 240 }}>
                                    <label className="filter-label">Especies</label>
                                    <Dropdown
                                        options={especies}
                                        value={selectedEspecies}
                                        onChange={setSelectedEspecies}
                                        placeholder="Todas las especies"
                                        searchable={true}
                                        multiple={true}
                                    />
                                </div>
                                <div className="filter-group">
                                    <label className="filter-label">
                                        Seleccione un rango de fechas
                                    </label>
                                    <DateRangePicker
                                        startDate={dateRange.startDate}
                                        endDate={dateRange.endDate}
                                        onChange={setDateRange}
                                    />
                                </div>
                                {canSeeCapturas && (
                                    <div className="filter-group">
                                        <label className="filter-label">&nbsp;</label>
                                        <label
                                            style={{
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '6px',
                                                cursor: 'pointer',
                                                height: '38px',
                                            }}
                                        >
                                            <input
                                                type="checkbox"
                                                checked={showHeatmap}
                                                onChange={(e) => setShowHeatmap(e.target.checked)}
                                            />
                                            Mapa de calor
                                        </label>
                                    </div>
                                )}
                            </div>

                            {cotoMsg && (
                                <div style={{
                                    fontSize: '13px',
                                    color: '#92400e',
                                    background: '#fff7e6',
                                    padding: '6px 12px',
                                    borderRadius: '4px',
                                }}>
                                    {cotoMsg}
                                </div>
                            )}
                        </div>

                        {error && (
                            <div style={{
                                padding: '20px',
                                backgroundColor: '#fee',
                                color: '#c33',
                                borderRadius: '8px',
                                marginBottom: '20px'
                            }}>
                                {error}
                            </div>
                        )}
                        {loadingGeo && (
                            <div style={{
                                padding: '20px',
                                textAlign: 'center',
                                color: '#666',
                                position: 'absolute',
                                top: 46,
                                left: 0,
                                right: 0,
                                zIndex: 1000,
                                backgroundColor: 'rgba(255,255,255,0.9)'
                            }}>
                                Cargando geometrías...
                            </div>
                        )}
                        <div style={{ position: 'relative' }}>
                            <div
                                ref={mapRef}
                                style={{
                                    height: "600px",
                                    width: "100%",
                                    borderRadius: "8px",
                                    overflow: "hidden",
                                }}
                            />
                            {loadingCoto && (
                                <div style={{
                                    position: 'absolute',
                                    top: '50%',
                                    left: '50%',
                                    transform: 'translate(-50%, -50%)',
                                    zIndex: 1000,
                                    background: 'rgba(255,255,255,0.96)',
                                    padding: '14px 22px',
                                    borderRadius: '10px',
                                    boxShadow: '0 4px 14px rgba(0,0,0,0.18)',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    fontSize: '14px',
                                    color: '#0f5e52',
                                    fontWeight: 600,
                                }}>
                                    <span className="visor-spinner" />
                                    Cargando coto…
                                </div>
                            )}
                        </div>
                        <style>{`
                            .visor-spinner {
                                width: 18px;
                                height: 18px;
                                border: 3px solid #c9e4de;
                                border-top-color: #1C8153;
                                border-radius: 50%;
                                animation: visor-spin 0.8s linear infinite;
                                display: inline-block;
                            }
                            @keyframes visor-spin {
                                to { transform: rotate(360deg); }
                            }
                        `}</style>
                        {canSeeCapturas && !loadingGeo && geometrias && geometrias.features && (
                            <div style={{
                                marginTop: '10px',
                                padding: '12px',
                                background: '#e8f4f8',
                                borderRadius: '4px',
                                fontSize: '14px',
                                color: '#0066cc',
                                textAlign: 'center'
                            }}>
                                <strong>{geometrias.features.length}</strong> capturas en el mapa · Temporada {temporada}/{temporada + 1}
                            </div>
                        )}
                        {!canSeeCapturas && (
                            <div style={{
                                marginTop: '10px',
                                padding: '12px',
                                background: '#fff7e6',
                                borderRadius: '4px',
                                fontSize: '13px',
                                color: '#92400e',
                                textAlign: 'center'
                            }}>
                                Haz clic sobre un coto o seleccionalo por matrícula para ver el total de capturas en la temporada (o rango de fechas) seleccionada.
                            </div>
                        )}
                    </div>
                </Card>
            </div>
        </div>
    );
}
